import { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { useAppLocale } from '../../../hooks/useAppLocale';
import {
  Badge,
  Box,
  Typography,
  CircularProgress,
  Button,
  useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { VariableSizeList as List, ListChildComponentProps } from 'react-window';
import { MessageBubble } from '../MessageBubble';
import { TypingIndicator } from '../TypingIndicator';
import { EmptyConversationState } from '../EmptyConversationState';
import type { Message, ChannelType } from '../../../types/chat.types';

// Import WhatsApp-style backgrounds
import waBackgroundLight from '../../../assets/wa-background-light.png';
import waBackgroundDark from '../../../assets/wa-background-dark.png';

interface TypingUser {
  userId: number;
  email?: string;
}

interface RealtimeReaction {
  reaction: string;
  userId?: number;
  contactIdentityId?: number;
}

interface MessageThreadProps {
  messages: Message[];
  typingUsers?: TypingUser[];
  currentUserId: number;
  isLoading?: boolean;
  hasMoreMessages?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /** Real-time reactions from WebSocket, keyed by messageId */
  realtimeReactions?: Map<number, RealtimeReaction[]>;
  /** Channel type for determining message actions (edit/delete/react) */
  channelType?: ChannelType | null;
  /** Callback when a message is updated (edit/delete/react) */
  onMessageUpdated?: () => void;
  /** Callback to retry sending a failed message */
  onRetryMessage?: (messageId: number) => void;
  /** Callback when user wants to reply/quote a message */
  onReply?: (message: Message) => void;
  /** Callback when user wants to forward a message */
  onForward?: (message: Message) => void;
}

interface MessageItem {
  type: 'date-separator' | 'message';
  date?: string;
  message?: Message;
  isOwnMessage?: boolean;
}

const DATE_SEPARATOR_HEIGHT = 40;
const MESSAGE_BASE_HEIGHT = 100; // Accounts for sender name, timestamp, padding
const MESSAGE_VERTICAL_MARGIN = 16; // my={1} = 8px top + 8px bottom
const TYPING_INDICATOR_HEIGHT = 48;

// Format date for display (locale-aware)
const formatDateSeparator = (dateString: string, translate: (key: string) => string, locale?: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return translate('resources.chat.time.today');
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return translate('resources.chat.time.yesterday');
  }

  return date.toLocaleDateString(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
};

// Check if two dates are on different days
const isDifferentDay = (date1: string, date2: string): boolean => {
  return new Date(date1).toDateString() !== new Date(date2).toDateString();
};

// Fixed thumbnail sizes matching MessageAttachment component
const STICKER_THUMBNAIL_SIZE = 150;
const IMAGE_VIDEO_THUMBNAIL_SIZE = 200;

// Estimate message height based on content
// We intentionally over-estimate to prevent overlap - extra space is better than overlap
const estimateMessageHeight = (message: Message): number => {
  let height = MESSAGE_BASE_HEIGHT;

  // Add height for longer text
  if (message.body) {
    const lines = Math.ceil(message.body.length / 40); // More conservative line estimate
    height += Math.min(lines * 24, 250);
  }

  // Check message type for media messages (in case attachments don't have correct fileType)
  const isMediaMessage =
    message.type === 'sticker' ||
    message.type === 'image' ||
    message.type === 'video';

  // Add height for attachments - using fixed thumbnail sizes
  if (message.attachments && message.attachments.length > 0) {
    message.attachments.forEach((attachment) => {
      // Determine if this is a sticker (check both attachment.fileType and message.type)
      const isSticker = attachment.fileType === 'sticker' || message.type === 'sticker';
      const isImageOrVideo =
        attachment.fileType === 'image' ||
        attachment.fileType === 'video' ||
        attachment.fileType === 'gif' ||
        message.type === 'image' ||
        message.type === 'video';

      // Image/video/sticker attachments - fixed thumbnail sizes
      // Extra height accounts for: type label row (~32px), attachment container margins (~16px)
      if (isSticker) {
        height += STICKER_THUMBNAIL_SIZE + 50;
      } else if (isImageOrVideo) {
        height += IMAGE_VIDEO_THUMBNAIL_SIZE + 50;
      }
      // Audio attachments
      else if (attachment.fileType === 'audio') {
        height += 80;
      }
      // PDF documents (card style) vs other documents (chip style)
      else if (
        attachment.fileType === 'document' &&
        (attachment.mimeType === 'application/pdf' ||
          attachment.fileName?.toLowerCase().endsWith('.pdf'))
      ) {
        height += 75;
      }
      // Other document/file attachments (chip style)
      else {
        height += 50;
      }

      // Add extra space for caption if present
      if (attachment.caption) {
        height += 40;
      }
    });
  } else if (isMediaMessage) {
    // Message type is media but no attachments array - still need to account for media height
    if (message.type === 'sticker') {
      height += STICKER_THUMBNAIL_SIZE + 50;
    } else {
      height += IMAGE_VIDEO_THUMBNAIL_SIZE + 50;
    }
  }

  // Add height for quoted message (check both resolved object and ID)
  if (message.quotedMessage || message.quotedMessageId) {
    height += 80;
  }

  // Add height for reactions if present
  if (message.reactions && message.reactions.length > 0) {
    height += 40;
  }

  // Add vertical margin between messages
  height += MESSAGE_VERTICAL_MARGIN;

  return height;
};

// Date separator component
const DateSeparator = ({ date }: { date: string }) => (
  <Box display="flex" justifyContent="center" py={1.5}>
    <Typography
      variant="caption"
      color="textSecondary"
      sx={{
        bgcolor: 'action.selected',
        px: 2,
        py: 0.5,
        borderRadius: 2,
        fontSize: 11,
        fontWeight: 500,
      }}
    >
      {date}
    </Typography>
  </Box>
);

// Row renderer for react-window
const Row = ({
  index,
  style,
  data,
}: ListChildComponentProps<{
  items: MessageItem[];
  currentUserId: number;
  realtimeReactions?: Map<number, RealtimeReaction[]>;
  channelType?: ChannelType | null;
  onMessageUpdated?: () => void;
  onRetryMessage?: (messageId: number) => void;
  onReply?: (message: Message) => void;
  onForward?: (message: Message) => void;
}>) => {
  const item = data.items[index];
  const messageReactions = item.message?.id
    ? data.realtimeReactions?.get(item.message.id)
    : undefined;

  return (
    <div style={style}>
      {item.type === 'date-separator' && item.date && (
        <DateSeparator date={item.date} />
      )}
      {item.type === 'message' && item.message && (
        <Box px={2}>
          <MessageBubble
            message={item.message}
            isOwnMessage={item.isOwnMessage ?? false}
            realtimeReactions={messageReactions}
            currentUserId={data.currentUserId}
            channelType={data.channelType}
            onMessageUpdated={data.onMessageUpdated}
            onRetryMessage={data.onRetryMessage}
            onReply={data.onReply}
            onForward={data.onForward}
          />
        </Box>
      )}
    </div>
  );
};

export function MessageThread({
  messages,
  typingUsers = [],
  currentUserId,
  isLoading = false,
  hasMoreMessages = false,
  onLoadMore,
  isLoadingMore = false,
  realtimeReactions,
  channelType,
  onMessageUpdated,
  onRetryMessage,
  onReply,
  onForward,
}: MessageThreadProps) {
  const translate = useTranslate();
  const appLocale = useAppLocale();
  const theme = useTheme();
  const listRef = useRef<List>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const prevMessagesLengthRef = useRef(messages.length);
  const hasScrolledToBottomRef = useRef(false);
  const prevMessageIdsRef = useRef<string>('');

  // Select appropriate background based on theme
  const waBackground = theme.palette.mode === 'dark' ? waBackgroundDark : waBackgroundLight;
  const isAtBottomRef = useRef(true);
  // State-driven visibility for scroll-to-bottom button (ref alone doesn't trigger re-renders)
  const [showScrollButton, setShowScrollButton] = useState(false);
  // Count of new messages received while user is scrolled up
  const [newMessageCount, setNewMessageCount] = useState(0);

  // Build items array with date separators
  const items: MessageItem[] = useMemo(() => {
    const result: MessageItem[] = [];

    messages.forEach((message, index) => {
      const prevMessage = index > 0 ? messages[index - 1] : null;
      const showDateSeparator =
        !prevMessage || isDifferentDay(message.createdAt, prevMessage.createdAt);

      if (showDateSeparator) {
        result.push({
          type: 'date-separator',
          date: formatDateSeparator(message.createdAt, translate, appLocale),
        });
      }

      result.push({
        type: 'message',
        message,
        // A message is "own" if sent by current user OR if fromMe is true (sent from phone/business side)
        isOwnMessage: message.senderUserId === currentUserId || message.fromMe === true,
      });
    });

    return result;
  }, [messages, currentUserId, translate, appLocale]);

  // Calculate item height
  const getItemSize = useCallback(
    (index: number): number => {
      const item = items[index];
      if (item.type === 'date-separator') {
        return DATE_SEPARATOR_HEIGHT;
      }
      if (item.message) {
        return estimateMessageHeight(item.message);
      }
      return MESSAGE_BASE_HEIGHT;
    },
    [items]
  );

  // Handle container resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateHeight = () => {
      const height = container.getBoundingClientRect().height;
      if (height > 0) {
        setContainerHeight(height);
      }
    };

    // Set initial height immediately
    updateHeight();

    // Also try after a short delay (for layout settling)
    const timeoutId = setTimeout(updateHeight, 100);

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        if (height > 0) {
          setContainerHeight(height);
        }
      }
    });

    resizeObserver.observe(container);
    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, []);

  // Recalculate height when messages arrive (container may have been 0 initially)
  useEffect(() => {
    if (messages.length > 0 && containerHeight === 0) {
      const container = containerRef.current;
      if (container) {
        const height = container.getBoundingClientRect().height;
        if (height > 0) {
          setContainerHeight(height);
        }
      }
    }
  }, [messages.length, containerHeight]);

  // Detect conversation switch by tracking the first message ID
  // When the conversation changes, the first message ID will be different
  useEffect(() => {
    const firstMessageId = messages.length > 0 ? String(messages[0].id) : '';
    if (firstMessageId && firstMessageId !== prevMessageIdsRef.current) {
      // Conversation changed — reset scroll tracking
      hasScrolledToBottomRef.current = false;
      prevMessageIdsRef.current = firstMessageId;
      setShowScrollButton(false);
      setNewMessageCount(0);
    }
  }, [messages]);

  // Auto-scroll to bottom on initial load (when entering a conversation or F5)
  useEffect(() => {
    if (messages.length > 0 && items.length > 0 && listRef.current && !hasScrolledToBottomRef.current) {
      // Use multiple attempts to ensure the list is fully rendered
      const scrollToEnd = () => {
        if (listRef.current) {
          listRef.current.scrollToItem(items.length - 1, 'end');
          isAtBottomRef.current = true;
          hasScrolledToBottomRef.current = true;
          setShowScrollButton(false);
          setNewMessageCount(0);
        }
      };
      // First attempt after a short delay
      setTimeout(scrollToEnd, 100);
      // Second attempt after layout has settled
      setTimeout(scrollToEnd, 300);
    }
  }, [messages.length, items.length, containerHeight]);

  // Auto-scroll to bottom on new messages only if user is at bottom
  // If user is scrolled up reading history, show unread indicator instead
  useEffect(() => {
    const newCount = messages.length - prevMessagesLengthRef.current;
    const hasNewMessages = newCount > 0;
    prevMessagesLengthRef.current = messages.length;

    if (hasNewMessages && hasScrolledToBottomRef.current) {
      if (isAtBottomRef.current && listRef.current) {
        // User is at bottom — auto-scroll to new messages
        listRef.current.scrollToItem(items.length - 1, 'end');
      } else {
        // User is scrolled up — don't steal scroll position, show unread badge
        setNewMessageCount((prev) => prev + newCount);
        setShowScrollButton(true);
      }
    }
  }, [messages.length, items.length]);

  // Reset cache when messages change
  useEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [items]);

  // Track scroll position to determine if at bottom
  const handleScroll = useCallback(
    ({ scrollOffset, scrollUpdateWasRequested }: { scrollOffset: number; scrollUpdateWasRequested: boolean }) => {
      if (scrollUpdateWasRequested) return;

      // Calculate total content height
      let totalHeight = 0;
      for (let i = 0; i < items.length; i++) {
        totalHeight += getItemSize(i);
      }

      // Consider "at bottom" if within 100px of the end
      const atBottom = scrollOffset + containerHeight >= totalHeight - 100;
      const wasAtBottom = isAtBottomRef.current;
      isAtBottomRef.current = atBottom;

      // Only update state when the value actually changes to avoid unnecessary re-renders
      if (atBottom !== wasAtBottom) {
        setShowScrollButton(!atBottom);
        if (atBottom) {
          setNewMessageCount(0);
        }
      }

      // Trigger load more when scrolled near top
      if (scrollOffset < 100 && hasMoreMessages && !isLoadingMore && onLoadMore) {
        onLoadMore();
      }
    },
    [containerHeight, items.length, getItemSize, hasMoreMessages, isLoadingMore, onLoadMore]
  );

  // Scroll to bottom programmatically
  const scrollToBottom = useCallback(() => {
    if (listRef.current && items.length > 0) {
      listRef.current.scrollToItem(items.length - 1, 'end');
      isAtBottomRef.current = true;
      setShowScrollButton(false);
      setNewMessageCount(0);
    }
  }, [items.length]);

  // Loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex={1}
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Empty state - with WhatsApp background
  if (messages.length === 0) {
    return (
      <EmptyConversationState
        title={translate('resources.chat.noMessages')}
        subtitle={translate('resources.chat.startConversationHint')}
      />
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: waBackground ? 'transparent' : 'background.default',
        backgroundImage: waBackground ? `url(${waBackground})` : undefined,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
      }}
    >
      {/* Load more indicator */}
      {hasMoreMessages && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            display: 'flex',
            justifyContent: 'center',
            py: 1,
            bgcolor: theme.palette.background.overlay,
          }}
        >
          {isLoadingMore ? (
            <CircularProgress size={20} />
          ) : (
            <Button
              size="small"
              startIcon={<KeyboardArrowUpIcon />}
              onClick={onLoadMore}
              sx={{ textTransform: 'none' }}
            >
              {translate('resources.chat.loadMore')}
            </Button>
          )}
        </Box>
      )}

      {/* Virtual scrolling message list */}
      {containerHeight > 0 && (
        <List
          ref={listRef}
          height={containerHeight - (typingUsers.length > 0 ? TYPING_INDICATOR_HEIGHT : 0)}
          width="100%"
          itemCount={items.length}
          itemSize={getItemSize}
          itemData={{ items, currentUserId, realtimeReactions, channelType, onMessageUpdated, onRetryMessage, onReply, onForward }}
          onScroll={handleScroll}
          overscanCount={5}
          style={{ paddingTop: hasMoreMessages ? 40 : 0 }}
        >
          {Row}
        </List>
      )}

      {/* Typing indicator */}
      {typingUsers.length > 0 && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <TypingIndicator users={typingUsers} />
        </Box>
      )}

      {/* Scroll to bottom button (shown when not at bottom) */}
      {showScrollButton && (
        <Box
          sx={{
            position: 'absolute',
            bottom: typingUsers.length > 0 ? TYPING_INDICATOR_HEIGHT + 16 : 16,
            right: 16,
            zIndex: 2,
          }}
        >
          <Badge
            badgeContent={newMessageCount}
            color="primary"
            max={99}
            invisible={newMessageCount === 0}
          >
            <Button
              variant="contained"
              size="small"
              onClick={scrollToBottom}
              sx={{
                minWidth: 40,
                width: 40,
                height: 40,
                borderRadius: '50%',
                p: 0,
                bgcolor: 'background.paper',
                color: 'text.secondary',
                boxShadow: 2,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <KeyboardArrowDownIcon />
            </Button>
          </Badge>
        </Box>
      )}
    </Box>
  );
}
